import React, { useState, useEffect, useRef } from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../Layouts/DashboardLayout';
import ProfileStrengthForm from './ProfileStrengthForm';
import { GooglePlacesService } from '../../heatmap/lib/dataforseo-places';
import GoogleAdsKeywordService from '../../services/GoogleAdsKeywordService';

import logger from '../../utils/logger';

// Styled Components with Mobile-First Design
const PageContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  minHeight: '100vh',
  backgroundColor: '#F8FAFC',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
  },
}));

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: '20px',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '24px',
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '28px',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: '4px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '26px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '28px',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  color: '#64748b',
  lineHeight: 1.5,
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
  },
}));

const ProfileStrengthPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // DataForSEO credentials (can be in format "login:password" or separate env vars)
  const defaultApiKey = process.env.REACT_APP_GOOGLE_API_KEY ||
    `${process.env.REACT_APP_DATAFORSEO_LOGIN}:${process.env.REACT_APP_DATAFORSEO_PASSWORD}` || '';
  const places = new GooglePlacesService(defaultApiKey, true);

  // Business data state for keyword suggestions (same as HeatmapPage/CompetitorAnalysisModal)
  const [businessData, setBusinessData] = useState(null);
  const keywordsFetchedRef = useRef(false);

  const [formData, setFormData] = useState({
    selectedAccount: 'E2E Group Mohali , Punjab',
    selectedKeywords: '', // Will be populated dynamically
    location: 'A-611, Bestech Business Tower,\nSector 66, SAS Nagar, Punjab 160066',
    mapGrid: '25  pins 5*5',
    radiusValue: 2,
    radiusUnit: 'Km',
    center: { lat: 28.6139, lng: 77.2090 }, // Default to New Delhi, India
    schedule: {
      frequency: 'Weekly',
      day: 'Monday',
      time: '10 : 20',
      period: 'Am'
    }
  });

  // Fetch dynamic keywords when businessData is available (same as HeatmapPage/CompetitorAnalysisModal)
  useEffect(() => {
    const fetchDynamicKeywords = async () => {
      // Prevent duplicate fetches
      if (keywordsFetchedRef.current || !businessData) {
        return;
      }

      keywordsFetchedRef.current = true;

      try {
        logger.log('[ProfileStrengthPage] Fetching keywords for business:', businessData.name || businessData.title);

        // Extract business category from GMB data (same as CompetitorAnalysisModal)
        const businessCategory = GoogleAdsKeywordService.extractBusinessCategory(businessData);

        logger.log('[ProfileStrengthPage] Business category:', businessCategory);

        // Get real keywords from DataForSEO API (India only) - same as CompetitorAnalysisModal
        const keywords = await GoogleAdsKeywordService.getKeywordSuggestions({
          businessCategory,
          location: 'India'
        });

        // Extract search terms for selection
        const keywordTerms = keywords.slice(0, 6).map(k => k.searchTerm);

        logger.log('[ProfileStrengthPage] Fetched keywords:', keywordTerms);

        if (keywordTerms.length > 0) {
          // Set first 3 keywords as selected keywords for the form
          setFormData(prev => ({
            ...prev,
            selectedKeywords: keywordTerms.slice(0, 3).join(', ')
          }));
        }

      } catch (error) {
        logger.error('[ProfileStrengthPage] Error fetching dynamic keywords:', error);
      }
    };

    fetchDynamicKeywords();
  }, [businessData]);

  // Get coordinates from GMB business data (same approach as HeatmapPage)
  const getGMBCoordinates = async () => {
    try {
      logger.log('[ProfileStrengthPage] Getting GMB coordinates...');

      // Get selected business from localStorage
      const selectedBusiness = localStorage.getItem('selectedBusiness');
      if (!selectedBusiness) {
        logger.log('[ProfileStrengthPage] No business selected in localStorage');
        return null;
      }

      const business = JSON.parse(selectedBusiness);
      logger.log('[ProfileStrengthPage] Selected business from localStorage:', business);

      // Try multiple possible coordinate formats in the business object
      let lat = null;
      let lng = null;

      // Format 1: latlng object with latitude/longitude
      if (business.latlng?.latitude && business.latlng?.longitude) {
        lat = business.latlng.latitude;
        lng = business.latlng.longitude;
        logger.log('[ProfileStrengthPage] Found coordinates in latlng object:', { lat, lng });
      }
      // Format 2: Direct lat/lng properties
      else if (business.lat && business.lng) {
        lat = business.lat;
        lng = business.lng;
        logger.log('[ProfileStrengthPage] Found coordinates in direct lat/lng:', { lat, lng });
      }
      // Format 3: latitude/longitude properties
      else if (business.latitude && business.longitude) {
        lat = business.latitude;
        lng = business.longitude;
        logger.log('[ProfileStrengthPage] Found coordinates in latitude/longitude:', { lat, lng });
      }
      // Format 4: storefrontAddress with lat/lng
      else if (business.storefrontAddress?.latitude && business.storefrontAddress?.longitude) {
        lat = business.storefrontAddress.latitude;
        lng = business.storefrontAddress.longitude;
        logger.log('[ProfileStrengthPage] Found coordinates in storefrontAddress:', { lat, lng });
      }

      // If we found coordinates, return them and set businessData for keyword suggestions
      if (lat && lng && lat !== 0 && lng !== 0) {
        logger.log('[ProfileStrengthPage] Successfully found GMB coordinates:', { lat, lng });

        // Store full business data for keyword suggestions (same as HeatmapPage/CompetitorAnalysisModal)
        setBusinessData(business);
        logger.log('[ProfileStrengthPage] Set business data for keyword suggestions:', business.name || business.title);

        return { lat, lng };
      }

      logger.log('[ProfileStrengthPage] No valid coordinates found in GMB data');
      return null;
    } catch (error) {
      logger.error('[ProfileStrengthPage] Error getting GMB coordinates:', error);
      return null;
    }
  };

  // Update form data based on URL parameters from BusinessProfile
  useEffect(() => {
    const businessName = searchParams.get('businessName');
    const placeId = searchParams.get('placeId');
    const address = searchParams.get('address');
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));

    // Set business name if provided
    if (businessName) {
      setFormData(prev => ({
        ...prev,
        selectedAccount: businessName
      }));

      // Load business data from localStorage for keyword suggestions
      try {
        const selectedBusiness = localStorage.getItem('selectedBusiness');
        if (selectedBusiness) {
          const business = JSON.parse(selectedBusiness);
          setBusinessData(business);
          logger.log('[ProfileStrengthPage] Set business data from localStorage:', business.name || business.title);
        }
      } catch (e) {
        logger.error('[ProfileStrengthPage] Error loading business data:', e);
      }
    }

    // Set address if provided
    if (address) {
      setFormData(prev => ({
        ...prev,
        location: address
      }));
    }

    // Priority 1: If we have direct coordinates from URL, use them
    if (!isNaN(lat) && !isNaN(lng)) {
      logger.log('[ProfileStrengthPage] Using coordinates from URL:', { lat, lng });
      setFormData(prev => ({
        ...prev,
        center: { lat, lng }
      }));
      return;
    }

    // Priority 2: Try to get coordinates from GMB business data, then placeId, then geocoding
    async function resolveCenter() {
      // Try GMB coordinates first
      const gmbCoords = await getGMBCoordinates();
      if (gmbCoords) {
        logger.log('[ProfileStrengthPage] Using GMB coordinates:', gmbCoords);
        setFormData(prev => ({
          ...prev,
          center: gmbCoords
        }));
        return;
      }

      // Try placeId if no GMB coordinates
      if (!placeId) return;

      try {
        logger.log('Attempting to resolve coordinates for placeId:', placeId);
        const coords = await places.getCoordinatesByPlaceId(placeId);
        logger.log('Resolved coordinates:', coords);

        if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
          setFormData(prev => ({
            ...prev,
            center: { lat: coords.lat, lng: coords.lng },
            location: address || `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`
          }));
        } else {
          logger.warn('Invalid coordinates received:', coords);
        }
      } catch (e) {
        logger.error('Failed to resolve center by placeId:', e);
        // Fallback to default coordinates if available
        if (address) {
          try {
            const coords = await places.geocodeAddress(address);
            if (coords) {
              setFormData(prev => ({
                ...prev,
                center: coords,
                location: address
              }));
            }
          } catch (geocodeError) {
            logger.error('Geocoding fallback failed:', geocodeError);
          }
        }
      }
    }

    resolveCenter();
  }, [searchParams]);

  // Load business data for default business on initial load
  useEffect(() => {
    // Load business data from localStorage if no businessName in URL and keywords not yet fetched
    if (!searchParams.get('businessName') && !businessData && !keywordsFetchedRef.current) {
      try {
        const selectedBusiness = localStorage.getItem('selectedBusiness');
        if (selectedBusiness) {
          const business = JSON.parse(selectedBusiness);
          setBusinessData(business);
          logger.log('[ProfileStrengthPage] Set default business data from localStorage:', business.name || business.title);
        }
      } catch (e) {
        logger.error('[ProfileStrengthPage] Error loading default business data:', e);
      }
    }
  }, [searchParams, businessData]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContinue = async () => {
    logger.log('Continue clicked with data:', formData);

    // Ensure we have business coordinates before navigating
    let centerCoords = formData.center;

    // If no center coordinates, try to get from selected business
    if (!centerCoords) {
      logger.log('No center coordinates, attempting to get from selected business...');
      try {
        // First try to get from selectedBusiness in localStorage (saved by BusinessProfile)
        const selectedBusiness = localStorage.getItem('selectedBusiness');
        if (selectedBusiness) {
          const business = JSON.parse(selectedBusiness);
          logger.log('Found selected business in localStorage:', business);

          // Try multiple coordinate formats
          let lat = null;
          let lng = null;

          if (business.latlng?.latitude && business.latlng?.longitude) {
            lat = business.latlng.latitude;
            lng = business.latlng.longitude;
          } else if (business.lat && business.lng) {
            lat = business.lat;
            lng = business.lng;
          } else if (business.latitude && business.longitude) {
            lat = business.latitude;
            lng = business.longitude;
          } else if (business.storefrontAddress?.latitude && business.storefrontAddress?.longitude) {
            lat = business.storefrontAddress.latitude;
            lng = business.storefrontAddress.longitude;
          }

          if (lat && lng && lat !== 0 && lng !== 0) {
            centerCoords = { lat, lng };
            logger.log('Got coordinates from selected business:', centerCoords);
          }
        }

        // Fallback: try gmbAccounts if selectedBusiness didn't work
        if (!centerCoords && formData.selectedAccount) {
          const accounts = JSON.parse(localStorage.getItem('gmbAccounts') || '[]');
          const selectedAccount = accounts.find(acc =>
            acc.name === formData.selectedAccount ||
            acc.title === formData.selectedAccount
          );

          if (selectedAccount) {
            const lat = selectedAccount.latlng?.latitude || selectedAccount.storefrontAddress?.latitude;
            const lng = selectedAccount.latlng?.longitude || selectedAccount.storefrontAddress?.longitude;

            if (lat && lng && lat !== 0 && lng !== 0) {
              centerCoords = { lat, lng };
              logger.log('Got coordinates from gmbAccounts fallback:', centerCoords);
            }
          }
        }
      } catch (e) {
        logger.error('Failed to get coordinates from localStorage:', e);
      }
    }

    // Navigate to heatmap results page with the configuration
    const qs = new URLSearchParams();
    qs.set('businessName', formData.selectedAccount);
    const firstKeyword = String(formData.selectedKeywords || '').split(',')[0]?.trim() || '';
    qs.set('keyword', firstKeyword);
    // Extract grid dimension from label like "25 pins 5*5" -> 5
    const match = /([0-9]+)\*([0-9]+)/.exec(formData.mapGrid);
    const gridDim = match ? Number(match[1]) : 5;
    qs.set('gridSize', String(gridDim));
    const radiusKm = formData.radiusUnit === 'Miles' ? (Number(formData.radiusValue) * 1.60934) : Number(formData.radiusValue);
    qs.set('radius', String(radiusKm));

    // Always include coordinates (from formData.center or GMB account)
    if (centerCoords) {
      qs.set('lat', String(centerCoords.lat));
      qs.set('lng', String(centerCoords.lng));
      logger.log('Using coordinates for heatmap:', centerCoords);
    } else {
      logger.warn('⚠️ No coordinates available - heatmap will use default location');
    }

    // Navigate to heatmap results
    navigate(`/heatmap-results?${qs.toString()}`);
  };

  return (
    <DashboardLayout>
      <PageContainer>
        <PageHeader>
          <PageTitle>Profile Strength</PageTitle>
          <PageSubtitle>
            Configure your heatmap analysis to track your business visibility
          </PageSubtitle>
        </PageHeader>
        <ProfileStrengthForm
          formData={formData}
          onFormChange={handleFormChange}
          onContinue={handleContinue}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      </PageContainer>
    </DashboardLayout>
  );
};

export default ProfileStrengthPage;
